import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

interface OnboardingSteps {
  accountSetUp: boolean;
  supplierCreated: boolean;
  billCreated: boolean;
  billApproved: boolean;
}

interface ApOnboardingStatus {
  loading: boolean;
  steps: OnboardingSteps;
  completedCount: number;
  hasBeenOnboarded: boolean;
  refresh: () => Promise<void>;
}

export function useApOnboardingStatus(): ApOnboardingStatus {
  const { activeOrganisationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState<OnboardingSteps>({
    accountSetUp: false,
    supplierCreated: false,
    billCreated: false,
    billApproved: false,
  });
  const [hasBeenOnboarded, setHasBeenOnboarded] = useState(false);
  const fetchedRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    if (!activeOrganisationId) return;
    setLoading(true);

    try {
      const [orgRes, arRes, suppliersRes, billsRes, approvedBillsRes] = await Promise.all([
        api.GET("/v1/organisations/{id}", {
          params: { path: { id: activeOrganisationId } },
        }),
        api.GET("/v1/organisations/{organisationId}/settings/ar", {
          params: { path: { organisationId: activeOrganisationId } },
        }),
        api.GET("/v1/suppliers", { params: { query: { Limit: 1 } } }),
        api.GET("/v1/bills", { params: { query: { Limit: 1 } } }),
        api.GET("/v1/bills", { params: { query: { Limit: 1, States: ["Approved"] } } }),
      ]);

      const org = orgRes.data;
      // Bank details and payment terms live on the org's AR settings.
      const ar = arRes.data;
      setHasBeenOnboarded(!!org?.hasBeenOnboarded);
      setSteps({
        accountSetUp: !!(
          org?.name &&
          org?.contactEmail &&
          org?.country &&
          org?.businessType &&
          org?.industryId &&
          org?.taxNumber &&
          org?.companyRegistrationNumber &&
          org?.addressLine1 &&
          org?.city &&
          org?.postalCode &&
          ar?.bankDetails?.accountHolderName &&
          ar?.bankDetails?.country &&
          ar?.bankDetails?.currency &&
          (ar.bankDetails.accountNumber || ar.bankDetails.iban || ar.bankDetails.routingNumber) &&
          ar?.defaultPaymentTermsDays
        ),
        supplierCreated: (suppliersRes.data?.data?.length ?? 0) > 0,
        billCreated: (billsRes.data?.data?.length ?? 0) > 0,
        billApproved: (approvedBillsRes.data?.data?.length ?? 0) > 0,
      });
    } catch (e) {
      console.error("Failed to load AP onboarding status:", e);
    } finally {
      setLoading(false);
    }
  }, [activeOrganisationId]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchStatus();
  }, [fetchStatus]);

  const completedCount = Object.values(steps).filter(Boolean).length;

  return { loading, steps, completedCount, hasBeenOnboarded, refresh: fetchStatus };
}
